import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { MatTableDataSource } from '@angular/material/table';
import { formatDate } from 'src/app/shared/utils/date.util';
import { CartolaService } from '../../services/cartola.service';
import { Sort } from '@angular/material/sort';
import Swal from 'sweetalert2';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-billing-record',
  templateUrl: './billing-record.component.html',
  styleUrls: ['./billing-record.component.sass']
})
export class BillingRecordComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  emisores: any[] = [];
  expandedCardId: number | null = null;
  range = new FormGroup({
    start: new FormControl<Date | null>(new Date(), [Validators.required]),
    end: new FormControl<Date | null>(new Date(), [Validators.required]),
  });
  dataSource: MatTableDataSource<any> | undefined;
  facturas: any | undefined;
  formEmisor: FormGroup | undefined;
  filterForm = this.fb.group({
    rut_origen: [''],
    email: [''],
    numero_id: [''],
  });

  pageSize = 20;
  currentPage = 0;
  totalItems = 0;

  constructor(
    private fb: FormBuilder,
    private cartolaService: CartolaService,
    private spinnerService: SpinnerService,
    private s3UrlService: S3UrlService,
  ) {
    const ref = this.spinnerService.start();
    this.formEmisor = this.fb.group({
      emisor: new FormControl("", [Validators.required]),
    });
    this.getEmisores();
    this.search();
    this.spinnerService.stop(ref);
  }
  async getEmisores() {
    this.emisores = await this.cartolaService.getEmisores();
    this.emisores = this.emisores.concat([{ cuenta: 'Todos', rutemisor: 'TODOS' }]);
  }
  async search(resetPage: boolean = false) {
    if (resetPage) {
      this.currentPage = 0;
    }
    const ref = this.spinnerService.start();
    const { start, end } = this.range.value;
    const { emisor } = this.formEmisor?.value;
    const { rut_origen, email, numero_id } = this.filterForm.value;

    const params = {
      startDate: formatDate(start),
      endDate: formatDate(end),
      rutEmisor: emisor || 'TODOS',
      rut_origen: rut_origen || undefined,
      email: email || undefined,
      numero_id: numero_id || undefined,
      page: this.currentPage + 1,
      limit: this.pageSize
    };

    try {
      const response = await this.cartolaService.getBillingRecords(params);
      this.dataSource = new MatTableDataSource(response.items);
      this.totalItems = response.total;
      const totalPages = Math.ceil(this.totalItems / this.pageSize);
      if (this.currentPage >= totalPages) {
        this.currentPage = 0;
      }
      this.paginator.pageIndex = this.currentPage;
    } finally {
      this.spinnerService.stop(ref);
    }
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  displayedColumns: string[] = [
    'cartola_id',
    'codigo_transferencia',
    'rut_origen',
    'email',
    'nombre_origen',
    'mo_saldo_abono',
    'numero_id',
    'doc_fiscal_id',
    'tipo_doc_fiscal',
    'emisor',
    'fecha_emision',
    'ver_pdf'
  ];

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  sortData(sort: Sort) {
    const data = this.facturas.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource = new MatTableDataSource(data);
    }

    this.facturas = data.sort((a: { cartola_id: number; }, b: { cartola_id: string; }) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'cartola_id':
          return this.compare(a.cartola_id, b.cartola_id, isAsc);
        default:
          return 0;
      }
    });
    this.dataSource = new MatTableDataSource(this.facturas);
  }

  async verPdf(row: any) {
    if (row.doc_fiscal_id && row.usuario_id) {
      const fileName = `${row.usuario_id + '-' + row.doc_fiscal_id}.pdf`;
      const region = 'us-east-1';
      const bucketName = 'aguacatebilling';
      // const url = `https://${bucketName}.s3.${region}.amazonaws.com/${fileName}`;
      const url = await this.generateSignedUrl(fileName);
      window.open(url, '_blank');

    } else {
      Swal.fire({
        icon: 'info',
        title: 'Esta factura no se encuentra disponible',
        text: 'Este registro no tiene una boleta o factura asociada aún.',
        confirmButtonText: 'Entendido'
      });
    }

  }

  /**
   * Post-VULN-01: backend firma contra el bucket `aguacatebilling`
   * (distinto del bucket KYC). El `S3UrlService` acepta el flag
   * `bucket: 'billing'` para resolver al bucket correcto server-side.
   */
  async generateSignedUrl(fileName: string): Promise<string> {
    const url = await this.s3UrlService.getReadUrl(fileName, 'billing');
    return url ?? '';
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

}
